// src/pages/IncidentsPage.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Input,
  Button,
  Select,
  Tag,
  Space,
  Typography,
  Tooltip,
  Empty,
  Drawer,
  Divider,
  Card,
  Modal,
  message,
  Spin,
  Pagination,
  Badge,
} from "antd";
import {
  ReloadOutlined,
  SearchOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  WarningOutlined,
  InfoCircleOutlined,
  ThunderboltOutlined,
  LikeOutlined,
  DislikeOutlined,
  RedoOutlined,
  EyeOutlined,
  HistoryOutlined,
} from "@ant-design/icons";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { incidentApi } from "../services/incidentApi";
import { resolveAlertDisplayName } from "../utils/alertDisplayHelpers";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";

const { Text, Paragraph } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const normalizeFilterValue = (value) => String(value || "").trim().toLowerCase();

const filterUniqueAlerts = (alerts, { severityFilter, statusFilter, searchQuery }) =>
  (alerts || []).filter((alert) => {
    if (severityFilter && normalizeFilterValue(alert?.latest_severity) !== normalizeFilterValue(severityFilter)) {
      return false;
    }
    if (statusFilter && normalizeFilterValue(alert?.latest_status) !== normalizeFilterValue(statusFilter)) {
      return false;
    }
    const query = normalizeFilterValue(searchQuery);
    if (query && !String(alert?.alert_name || "").toLowerCase().includes(query)) {
      return false;
    }
    return true;
  });

// ==================== Design System (matching AlertRulesView) ====================

const getSeverityConfig = (severity, isDark) => {
  switch (severity?.toLowerCase()) {
    case "critical":
      return {
        color: "#ff4d4f",
        bg: isDark ? "#2d1b1b" : "#fff2f0",
        iconBg: isDark ? "#3d2525" : "#ffccc7",
        icon: <CloseCircleOutlined style={{ fontSize: 20, color: "#ff4d4f" }} />,
      };
    case "warning":
      return {
        color: "#fa8c16",
        bg: isDark ? "#2d241b" : "#fff7e6",
        iconBg: isDark ? "#3d2f25" : "#ffe7ba",
        icon: <WarningOutlined style={{ fontSize: 20, color: "#fa8c16" }} />,
      };
    case "info":
      return {
        color: "#1890ff",
        bg: isDark ? "#1c2833" : "#e6f7ff",
        iconBg: isDark ? "#253340" : "#bae7ff",
        icon: <InfoCircleOutlined style={{ fontSize: 20, color: "#1890ff" }} />,
      };
    default:
      return {
        color: isDark ? "#adbac7" : "#8c8c8c",
        bg: isDark ? "#21262d" : "#fafafa",
        iconBg: isDark ? "#30363d" : "#f0f0f0",
        icon: <InfoCircleOutlined style={{ fontSize: 20, color: isDark ? "#adbac7" : "#8c8c8c" }} />,
      };
  }
};

const getStatusConfig = (status, isDark) => {
  switch (status?.toLowerCase()) {
    case "completed":
      return { color: "#52c41a", label: "Active", dotColor: "#52c41a", textColor: isDark ? "#73d13d" : "#52c41a" };
    case "resolved":
      return { color: "#1890ff", label: "Resolved", dotColor: "#1890ff", textColor: isDark ? "#40a9ff" : "#1890ff" };
    case "generating":
      return { color: "#fa8c16", label: "Generating", dotColor: "#fa8c16", textColor: isDark ? "#ffa940" : "#fa8c16" };
    case "processing":
      return { color: "#fa8c16", label: "Processing", dotColor: "#fa8c16", textColor: isDark ? "#ffa940" : "#fa8c16" };
    case "failed":
      return { color: "#ff4d4f", label: "Failed", dotColor: "#ff4d4f", textColor: isDark ? "#ff7875" : "#ff4d4f" };
    default:
      return { color: isDark ? "#adbac7" : "#8c8c8c", label: "Unknown", dotColor: isDark ? "#adbac7" : "#8c8c8c", textColor: isDark ? "#adbac7" : "#8c8c8c" };
  }
};

const StatCard = ({ value, label, color, isActive, onClick, showDot, isDark }) => (
  <div
    onClick={onClick}
    style={{
      background: isDark ? "#161b22" : "#fff",
      borderRadius: 12,
      padding: "20px 24px",
      cursor: onClick ? "pointer" : "default",
      border: isActive ? `2px solid ${color || "#1890ff"}` : `2px solid ${isDark ? "#30363d" : (color || "#e8e8e8")}`,
      boxShadow: isActive ? "0 2px 8px rgba(0,0,0,0.08)" : "none",
      transition: "all 0.2s",
      textAlign: "center",
      flex: 1,
    }}
  >
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
      <span style={{ fontSize: 28, fontWeight: 700, color: color || (isDark ? "#e6edf3" : "#262626") }}>
        {value}
      </span>
      {showDot && value > 0 && (
        <span style={{
          width: 8, height: 8, borderRadius: "50%", background: color,
          animation: "pulse 2s infinite",
        }} />
      )}
    </div>
    <div style={{ fontSize: 14, color: isDark ? "#adbac7" : "#8c8c8c", marginTop: 4 }}>{label}</div>
  </div>
);

const UniqueAlertCard = ({ alert, isDark, onView, onViewSimilar, onRerun }) => {
  const severityConfig = getSeverityConfig(alert.latest_severity, isDark);
  const statusConfig = getStatusConfig(alert.latest_status, isDark);
  const createdDate = alert.latest_created_at ? new Date(alert.latest_created_at) : null;

  return (
    <div
      style={{
        background: isDark ? "#161b22" : "#fff",
        borderRadius: 12,
        padding: "16px 20px",
        marginBottom: 12,
        border: `1px solid ${isDark ? "#30363d" : "#e8e8e8"}`,
        display: "flex",
        alignItems: "center",
        gap: 16,
        transition: "all 0.2s",
      }}
    >
      <div
        style={{
          width: 40, height: 40, borderRadius: "50%",
          background: severityConfig.iconBg,
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {severityConfig.icon}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
          <Text strong style={{ fontSize: 15, color: isDark ? "#e6edf3" : "#262626" }}>
            {alert.alert_name}
          </Text>
          <Tag color="blue" style={{ margin: 0, fontSize: 11 }}>
            {alert.incident_count} incident{alert.incident_count === 1 ? "" : "s"}
          </Tag>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: statusConfig.dotColor }} />
            <Text style={{ fontSize: 12, color: statusConfig.textColor }}>{statusConfig.label}</Text>
          </span>
          <Tag color={severityConfig.color} style={{ margin: 0, fontSize: 11 }}>
            {(alert.latest_severity || "unknown").toLowerCase()}
          </Tag>
          {createdDate && (
            <Text style={{ fontSize: 12, color: isDark ? "#8b949e" : "#8c8c8c" }}>
              Latest: {createdDate.toLocaleDateString()} {createdDate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
            </Text>
          )}
        </div>
      </div>

      <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
        <Tooltip title="View latest RCA">
          <Button type="text" size="small" icon={<EyeOutlined />} onClick={() => onView(alert)} />
        </Tooltip>
        <Tooltip title="Similar past incidents">
          <Button type="text" size="small" icon={<HistoryOutlined />} onClick={() => onViewSimilar(alert)} />
        </Tooltip>
        <Tooltip title="Re-run latest RCA">
          <Button type="text" size="small" icon={<RedoOutlined />} onClick={() => onRerun(alert)} />
        </Tooltip>
      </div>
    </div>
  );
};

const IncidentCard = ({ incident, isDark, onView, onViewSimilar, onResolve, onRerun }) => {
  const severityConfig = getSeverityConfig(incident.severity, isDark);
  const statusConfig = getStatusConfig(incident.status, isDark);
  const createdDate = incident.created_at ? new Date(incident.created_at) : null;

  return (
    <div
      style={{
        background: isDark ? "#161b22" : "#fff",
        borderRadius: 12,
        padding: "16px 20px",
        marginBottom: 12,
        border: `1px solid ${isDark ? "#30363d" : "#e8e8e8"}`,
        display: "flex",
        alignItems: "center",
        gap: 16,
        transition: "all 0.2s",
      }}
    >
      {/* Severity Icon */}
      <div
        style={{
          width: 40, height: 40, borderRadius: "50%",
          background: severityConfig.iconBg,
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {severityConfig.icon}
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
          <Text strong style={{ fontSize: 15, color: isDark ? "#e6edf3" : "#262626" }}>
            {resolveAlertDisplayName(incident)}
          </Text>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: statusConfig.dotColor }} />
            <Text style={{ fontSize: 12, color: statusConfig.textColor }}>{statusConfig.label}</Text>
          </span>
          <Tag color={severityConfig.color} style={{ margin: 0, fontSize: 11 }}>
            {(incident.severity || "unknown").toLowerCase()}
          </Tag>
          {createdDate && (
            <Text style={{ fontSize: 12, color: isDark ? "#8b949e" : "#8c8c8c" }}>
              {createdDate.toLocaleDateString()} {createdDate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
            </Text>
          )}
        </div>
        {incident.summary && (
          <Text
            style={{ fontSize: 12, color: isDark ? "#8b949e" : "#8c8c8c", marginTop: 4, display: "block" }}
            ellipsis={{ rows: 1 }}
          >
            {incident.summary.substring(0, 100)}...
          </Text>
        )}
      </div>

      {/* Actions */}
      <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
        <Tooltip title="View RCA">
          <Button type="text" size="small" icon={<EyeOutlined />} onClick={() => onView(incident)} />
        </Tooltip>
        <Tooltip title="Similar past incidents">
          <Button type="text" size="small" icon={<HistoryOutlined />} onClick={() => onViewSimilar(incident)} />
        </Tooltip>
        {incident.status !== "resolved" && (
          <Tooltip title="Resolve">
            <Button type="text" size="small" icon={<CheckCircleOutlined style={{ color: "#52c41a" }} />} onClick={() => onResolve(incident)} />
          </Tooltip>
        )}
        <Tooltip title="Re-run RCA">
          <Button type="text" size="small" icon={<RedoOutlined />} onClick={() => onRerun(incident)} />
        </Tooltip>
      </div>
    </div>
  );
};

// ==================== Main Page ====================

const IncidentsPage = () => {
  const { user } = useAuth();
  const { isDark } = useTheme();

  const [reports, setReports] = useState([]);
  const [totalReports, setTotalReports] = useState(0);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [similarIncidents, setSimilarIncidents] = useState([]);
  // Which section the drawer should emphasise: 'rca' (full report) or 'similar' (history).
  const [drawerFocus, setDrawerFocus] = useState("rca");
  const similarSectionRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [severityFilter, setSeverityFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [uniqueAlertsView, setUniqueAlertsView] = useState(false);
  const [resolveModalVisible, setResolveModalVisible] = useState(false);
  const [resolveNotes, setResolveNotes] = useState("");
  const [resolveReportId, setResolveReportId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const fetchReports = useCallback(async () => {
    if (uniqueAlertsView) {
      return;
    }
    setLoading(true);
    try {
      const data = await incidentApi.getReports({
        severity: severityFilter || undefined,
        status: statusFilter || undefined,
        search: debouncedSearch.trim() || undefined,
        page: currentPage,
        limit: pageSize,
      });
      setReports(data.reports || []);
      setTotalReports(data.total ?? data.reports?.length ?? 0);
    } catch (err) {
      message.error("Failed to load incidents");
    } finally {
      setLoading(false);
    }
  }, [uniqueAlertsView, severityFilter, statusFilter, debouncedSearch, currentPage, pageSize]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchStats = useCallback(async () => {
    try {
      const data = await incidentApi.getStats();
      setStats(data.stats);
    } catch (err) {
      console.error("Failed to fetch stats", err);
    }
  }, []);

  useEffect(() => {
    fetchReports();
    fetchStats();
  }, [fetchReports, fetchStats]);

  const openDetail = async (incident, focus = "rca") => {
    setDrawerVisible(true);
    setDetailLoading(true);
    setSimilarIncidents([]);
    setDrawerFocus(focus);
    try {
      const [reportData, similarData] = await Promise.all([
        incidentApi.getReport(incident.report_id),
        incidentApi.getSimilarIncidents(resolveAlertDisplayName(incident), 5),
      ]);
      setSelectedReport(reportData.report);
      setSimilarIncidents(similarData.incidents || []);
    } catch (err) {
      message.error("Failed to load incident details");
    } finally {
      setDetailLoading(false);
    }
  };

  const resolveLatestIncident = async (alertRow) => {
    if (alertRow?.latest_report_id) {
      return { report_id: alertRow.latest_report_id, alert_name: alertRow.alert_name };
    }
    const data = await incidentApi.getReports({ alertName: alertRow.alert_name, limit: 1 });
    return data.reports?.[0] || null;
  };

  const openUniqueAlertDetail = async (alertRow, focus = "rca") => {
    try {
      const latestIncident = await resolveLatestIncident(alertRow);
      if (!latestIncident?.report_id) {
        message.warning("No incident found for this alert");
        return;
      }
      await openDetail(latestIncident, focus);
    } catch (err) {
      message.error("Failed to load alert details");
    }
  };

  // When opened via "Similar", scroll the drawer to the Similar Past Incidents section.
  useEffect(() => {
    if (!detailLoading && drawerVisible && drawerFocus === "similar" && similarSectionRef.current) {
      similarSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [detailLoading, drawerVisible, drawerFocus, similarIncidents]);

  const filteredReports = reports;

  const filteredUniqueAlerts = useMemo(
    () => filterUniqueAlerts(stats?.alerts_by_name || [], { severityFilter, statusFilter, searchQuery }),
    [stats, severityFilter, statusFilter, searchQuery],
  );

  const displayReports = uniqueAlertsView ? filteredUniqueAlerts : filteredReports;

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, debouncedSearch, severityFilter, statusFilter, uniqueAlertsView]);

  const paginatedReports = uniqueAlertsView
    ? displayReports.slice((currentPage - 1) * pageSize, currentPage * pageSize)
    : displayReports;

  const paginationTotal = uniqueAlertsView ? displayReports.length : totalReports;

  const handleFeedback = async (reportId, rating) => {
    try {
      await incidentApi.submitFeedback(reportId, { rating, comment: "", userId: user?.email || "anonymous" });
      message.success(`Feedback submitted: ${rating === "thumbs_up" ? "👍" : "👎"}`);
    } catch (err) {
      message.error("Failed to submit feedback");
    }
  };

  const handleResolve = async () => {
    if (!resolveReportId) return;
    try {
      await incidentApi.resolveIncident(resolveReportId, { resolutionNotes: resolveNotes, resolvedBy: user?.email || "anonymous" });
      message.success("Incident marked as resolved");
      setResolveModalVisible(false);
      setResolveNotes("");
      setResolveReportId(null);
      fetchReports();
      fetchStats();
    } catch (err) {
      message.error("Failed to resolve incident");
    }
  };

  const handleRerun = async (incident) => {
    try {
      await incidentApi.rerunRca(incident.report_id);
      message.success("RCA re-run triggered");
    } catch (err) {
      message.error("Failed to re-run RCA");
    }
  };

  const rerunUniqueAlert = async (alertRow) => {
    try {
      const latestIncident = await resolveLatestIncident(alertRow);
      if (!latestIncident?.report_id) {
        message.warning("No incident found for this alert");
        return;
      }
      await handleRerun(latestIncident);
    } catch (err) {
      message.error("Failed to re-run RCA");
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24, flexShrink: 0 }}>
        <div>
          <Text strong style={{ fontSize: 22, color: isDark ? "#e6edf3" : "#262626" }}>Incidents</Text>
          <div style={{ color: isDark ? "#adbac7" : "#8c8c8c", fontSize: 14, marginTop: 4 }}>
            Auto-RCA reports from AI-powered root cause analysis
          </div>
        </div>
        <Tooltip title="Refresh">
          <Button
            icon={<ReloadOutlined />}
            loading={loading}
            onClick={() => { fetchReports(); fetchStats(); }}
            style={{ borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center" }}
          />
        </Tooltip>
      </div>

      {/* Stat Cards */}
      <div style={{ display: "flex", gap: 16, marginBottom: 24, flexShrink: 0 }}>
        <StatCard
          value={stats?.total_reports || 0}
          label="Total Incidents"
          color="#1890ff"
          isDark={isDark}
          isActive={!severityFilter && !statusFilter && !searchQuery.trim() && !uniqueAlertsView}
          onClick={() => {
            setSeverityFilter(null);
            setStatusFilter(null);
            setSearchQuery("");
            setUniqueAlertsView(false);
          }}
        />
        <StatCard
          value={stats?.by_severity?.critical || 0}
          label="Critical"
          color="#ff4d4f"
          showDot
          isDark={isDark}
          isActive={severityFilter === "critical" && !uniqueAlertsView}
          onClick={() => {
            setUniqueAlertsView(false);
            setSeverityFilter(severityFilter === "critical" ? null : "critical");
          }}
        />
        <StatCard
          value={stats?.by_severity?.warning || 0}
          label="Warning"
          color="#fa8c16"
          isDark={isDark}
          isActive={severityFilter === "warning" && !uniqueAlertsView}
          onClick={() => {
            setUniqueAlertsView(false);
            setSeverityFilter(severityFilter === "warning" ? null : "warning");
          }}
        />
        <StatCard
          value={stats?.unique_alerts || 0}
          label="Unique Alerts"
          color="#52c41a"
          isDark={isDark}
          isActive={uniqueAlertsView}
          onClick={() => {
            setUniqueAlertsView(true);
          }}
        />
        <StatCard
          value={stats?.by_severity?.info || 0}
          label="Info"
          color="#1890ff"
          isDark={isDark}
          isActive={severityFilter === "info" && !uniqueAlertsView}
          onClick={() => {
            setUniqueAlertsView(false);
            setSeverityFilter(severityFilter === "info" ? null : "info");
          }}
        />
      </div>

      {/* Search & Filters - static (stays visible) */}
      <div style={{ display: "flex", gap: 16, marginBottom: 24, alignItems: "center", flexShrink: 0 }}>
        <Input
          className="clair-alert-search-input"
          placeholder="Search by alert name, description, or labels..."
          prefix={<SearchOutlined style={{ color: isDark ? "#8b949e" : "#bfbfbf" }} />}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            flex: 1,
            borderRadius: 10,
            height: 44,
            background: isDark ? "#0d1117" : undefined,
            borderColor: isDark ? "#30363d" : "#d9d9d9",
            color: isDark ? "#e6edf3" : undefined,
          }}
          allowClear
        />
        <Select
          placeholder="All Severities"
          allowClear
          value={severityFilter}
          onChange={setSeverityFilter}
          style={{ width: 160, height: 44 }}
        >
          <Option value="critical">Critical</Option>
          <Option value="warning">Warning</Option>
          <Option value="info">Info</Option>
        </Select>
        <Select
          placeholder="All Status"
          allowClear
          value={statusFilter}
          onChange={setStatusFilter}
          style={{ width: 150, height: 44 }}
        >
          <Option value="completed">Active</Option>
          <Option value="resolved">Resolved</Option>
          <Option value="processing">Processing</Option>
          <Option value="failed">Failed</Option>
        </Select>
      </div>

      {/* Incident List - only this section scrolls */}
      <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: 48 }}><Spin size="large" /></div>
        ) : displayReports.length === 0 ? (
          <Empty
            description={
              uniqueAlertsView
                ? "No unique alerts match the selected filters"
                : severityFilter || statusFilter || searchQuery.trim()
                  ? "No incidents match the selected filters"
                  : "No incidents found"
            }
            style={{ padding: "48px 0" }}
          />
        ) : (
          paginatedReports.map((item) => (
            uniqueAlertsView ? (
              <UniqueAlertCard
                key={item.alert_name}
                alert={item}
                isDark={isDark}
                onView={() => openUniqueAlertDetail(item, "rca")}
                onViewSimilar={() => openUniqueAlertDetail(item, "similar")}
                onRerun={() => rerunUniqueAlert(item)}
              />
            ) : (
              <IncidentCard
                key={item.report_id}
                incident={item}
                isDark={isDark}
                onView={() => openDetail(item, "rca")}
                onViewSimilar={() => openDetail(item, "similar")}
                onResolve={() => { setResolveReportId(item.report_id); setResolveModalVisible(true); }}
                onRerun={() => handleRerun(item)}
              />
            )
          ))
        )}
      </div>

      {!loading && (uniqueAlertsView ? displayReports.length > 0 : totalReports > 0) && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 24, flexShrink: 0 }}>
          <Text style={{ color: isDark ? "#8b949e" : "#8c8c8c" }}>
            {uniqueAlertsView
              ? `Showing ${paginatedReports.length} of ${displayReports.length} unique alert${displayReports.length === 1 ? "" : "s"}`
              : severityFilter || statusFilter || debouncedSearch.trim()
                ? `Showing ${paginatedReports.length} of ${totalReports} matching incidents`
                : `Total ${totalReports} incidents`}
          </Text>
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            total={paginationTotal}
            onChange={setCurrentPage}
            showSizeChanger={false}
            size="small"
          />
        </div>
      )}

      {/* Detail Drawer */}
      <Drawer
        title={
          selectedReport ? (
            <Space>
              <Tag color={getSeverityConfig(selectedReport.severity, isDark).color}>
                {(selectedReport.severity || "").toUpperCase()}
              </Tag>
              <Text strong>{resolveAlertDisplayName(selectedReport)}</Text>
            </Space>
          ) : "Incident Detail"
        }
        placement="right"
        width={Math.min(window.innerWidth * 0.6, 800)}
        open={drawerVisible}
        onClose={() => { setDrawerVisible(false); setSelectedReport(null); setDrawerFocus("rca"); }}
        extra={
          selectedReport && (
            <Space>
              <Tooltip title="Helpful"><Button icon={<LikeOutlined />} onClick={() => handleFeedback(selectedReport.report_id, "thumbs_up")} /></Tooltip>
              <Tooltip title="Not helpful"><Button icon={<DislikeOutlined />} onClick={() => handleFeedback(selectedReport.report_id, "thumbs_down")} /></Tooltip>
              <Button icon={<RedoOutlined />} onClick={() => handleRerun(selectedReport)}>Re-run</Button>
            </Space>
          )
        }
      >
        {detailLoading ? (
          <div style={{ textAlign: "center", padding: 48 }}><Spin size="large" tip="Loading RCA analysis..." /></div>
        ) : selectedReport ? (
          <div>
            {/* Alert Context Card */}
            <Card
              size="small"
              style={{ marginBottom: 16, background: isDark ? "#21262d" : "#f6f8fa", borderColor: isDark ? "#30363d" : "#e8e8e8" }}
            >
              <Space direction="vertical" size={4} style={{ width: "100%" }}>
                <Text style={{ color: isDark ? "#8b949e" : "#8c8c8c" }}>
                  Started: {new Date(selectedReport.created_at).toLocaleString()}
                </Text>
                <Text style={{ color: isDark ? "#8b949e" : "#8c8c8c" }}>
                  Description: {selectedReport.description || "N/A"}
                </Text>
                {selectedReport.labels && Object.keys(selectedReport.labels).length > 0 && (
                  <div style={{ marginTop: 4 }}>
                    {Object.entries(selectedReport.labels).map(([k, v]) => (
                      <Tag key={k} style={{ marginBottom: 4, background: isDark ? "#30363d" : "#f0f0f0", borderColor: isDark ? "#484f58" : "#d9d9d9", color: isDark ? "#e6edf3" : undefined }}>
                        {k}={v}
                      </Tag>
                    ))}
                  </div>
                )}
              </Space>
            </Card>

            {/* Integrations (Jira / GitHub) */}
            {selectedReport.integrations && (() => {
              const integrations = selectedReport.integrations;
              const showIntegrations =
                integrations.jira_url ||
                integrations.github_pr_url ||
                (integrations.suspect_changes?.length > 0) ||
                integrations.remediation_status === "failed" ||
                (integrations.remediation_status === "skipped" && integrations.reason !== "jira_not_enabled");
              if (!showIntegrations) return null;
              const ticketLabel = integrations.jira_url
                ? "Ticket actions: done"
                : integrations.remediation_status === "failed"
                  ? "Ticket actions: failed"
                  : integrations.remediation_status === "skipped"
                    ? "Ticket actions: skipped"
                    : integrations.remediation_status === "pending"
                      ? "Ticket actions: pending"
                      : null;
              return (
              <>
                <Divider orientation="left" style={{ color: isDark ? "#e6edf3" : undefined }}>
                  Integrations
                </Divider>
                <Card size="small" style={{ marginBottom: 16, borderColor: isDark ? "#30363d" : "#e8e8e8" }}>
                  <Space direction="vertical" size={8}>
                    {ticketLabel && (
                      <Tag
                        title="Jira ticket integration status — not that the incident is resolved"
                        color={
                        integrations.remediation_status === "completed" || integrations.jira_url ? "green"
                          : integrations.remediation_status === "failed" ? "red" : "orange"
                      }>
                        {ticketLabel}
                      </Tag>
                    )}
                    {integrations.jira_url && (
                      <a href={integrations.jira_url} target="_blank" rel="noopener noreferrer">
                        Jira: {integrations.jira_issue_key}
                      </a>
                    )}
                    {integrations.github_pr_url && (
                      <a href={integrations.github_pr_url} target="_blank" rel="noopener noreferrer">
                        Draft remediation PR
                      </a>
                    )}
                    {integrations.suspect_changes?.length > 0 && (
                      <div>
                        <Text strong>Suspect code changes</Text>
                        <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                          {integrations.suspect_changes.map((ch, idx) => (
                            <li key={idx}>
                              {ch.url ? (
                                <a href={ch.url} target="_blank" rel="noopener noreferrer">
                                  [{ch.type}] {ch.message || ch.title}
                                </a>
                              ) : (
                                <span>[{ch.type}] {ch.message || ch.title}</span>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </Space>
                </Card>
              </>
              );
            })()}

            {/* RCA Content */}
            <Divider orientation="left" style={{ color: isDark ? "#e6edf3" : undefined }}>
              <ThunderboltOutlined /> AI Root Cause Analysis
            </Divider>
            <div
              className="rca-markdown-content"
              style={{ lineHeight: 1.7, color: isDark ? "#e6edf3" : "#24292f", fontSize: 14 }}
            >
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {selectedReport.content || "No RCA content available."}
              </ReactMarkdown>
            </div>

            {/* Similar Incidents */}
            <div ref={similarSectionRef}>
              <Divider orientation="left" style={{ color: isDark ? "#e6edf3" : undefined }}>
                <HistoryOutlined /> Similar Past Incidents ({similarIncidents.length})
              </Divider>
              {similarIncidents.length === 0 ? (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    <Text style={{ color: isDark ? "#8b949e" : "#8c8c8c" }}>
                      No other past incidents for "{resolveAlertDisplayName(selectedReport)}".
                    </Text>
                  }
                />
              ) : (
                similarIncidents.map((inc) => (
                  <Card
                    key={inc.report_id}
                    size="small"
                    hoverable
                    style={{ marginBottom: 8, background: isDark ? "#21262d" : undefined, borderColor: isDark ? "#30363d" : undefined }}
                    onClick={() => openDetail(inc, "rca")}
                  >
                    <Space>
                      <Tag color={getSeverityConfig(inc.severity, isDark).color}>{inc.severity}</Tag>
                      <Text style={{ color: isDark ? "#e6edf3" : undefined }}>{new Date(inc.created_at).toLocaleDateString()}</Text>
                      <Text style={{ color: isDark ? "#8b949e" : "#8c8c8c", fontSize: 12 }}>
                        {inc.summary?.substring(0, 60)}...
                      </Text>
                      {inc.resolved_at && <Tag color="green">Resolved</Tag>}
                    </Space>
                  </Card>
                ))
              )}
            </div>

            {/* Resolution Info */}
            {selectedReport.status === "resolved" && (
              <>
                <Divider orientation="left"><CheckCircleOutlined style={{ color: "#52c41a" }} /> Resolution</Divider>
                <Card size="small" style={{ background: isDark ? "#1c2128" : "#f6ffed", borderColor: isDark ? "#238636" : "#b7eb8f" }}>
                  <Text style={{ color: isDark ? "#e6edf3" : undefined }}>Resolved by: {selectedReport.resolved_by || "N/A"}</Text>
                  <br />
                  <Text style={{ color: isDark ? "#e6edf3" : undefined }}>Notes: {selectedReport.resolution_notes || "No notes"}</Text>
                </Card>
              </>
            )}
          </div>
        ) : null}
      </Drawer>

      {/* Resolve Modal */}
      <Modal
        title="Resolve Incident"
        open={resolveModalVisible}
        onOk={handleResolve}
        onCancel={() => { setResolveModalVisible(false); setResolveNotes(""); setResolveReportId(null); }}
        okText="Mark Resolved"
      >
        <Paragraph>What resolved this incident?</Paragraph>
        <TextArea
          rows={4}
          placeholder="Describe what fixed the issue (e.g., 'Scaled Glue DPUs from 5 to 10')"
          value={resolveNotes}
          onChange={(e) => setResolveNotes(e.target.value)}
        />
      </Modal>

      {/* CSS for pulse animation + search focus (matches Alert Rules) */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }

        .clair-alert-search-input:hover {
          border-color: #4096ff !important;
        }

        .clair-alert-search-input:focus,
        .clair-alert-search-input-focused,
        .clair-alert-search-input.ant-input-focused,
        .clair-alert-search-input.ant-input-affix-wrapper-focused {
          border-color: #1677ff !important;
          box-shadow: 0 0 0 2px rgba(5, 145, 255, 0.12) !important;
        }
      `}</style>
    </div>
  );
};

export default IncidentsPage;
